import React from 'react';

import type { GeoJSONFeatureCollection } from '../../types/gis';
import { featureCollectionToLayers } from '../../utils/featureCollectionToLayers';

interface LayerTogglePanelProps {
  fc: GeoJSONFeatureCollection;
  hiddenLayers: ReadonlySet<string>;
  onToggle: (layerName: string) => void;
  onFitToLayer: (layerName: string) => void;
  darkMode?: boolean;
}

/** Inline layer-toggle row. Shown below a map preview when the FC has more
 *  than one bucketed layer. Checkbox toggles visibility; clicking the
 *  layer name fits the viewport to that layer's bbox. */
export const LayerTogglePanel: React.FC<LayerTogglePanelProps> = ({
  fc, hiddenLayers, onToggle, onFitToLayer, darkMode,
}) => {
  const buckets = featureCollectionToLayers(fc);
  const layerNames = Object.keys(buckets);
  if (layerNames.length <= 1) return null;

  return (
    <div
      data-testid="layer-toggle-panel"
      className={`flex flex-wrap items-center gap-3 p-2 text-xs border-t ${
        darkMode
          ? 'bg-gray-800 border-gray-700 text-gray-300'
          : 'bg-gray-50 border-gray-200 text-gray-700'
      }`}
    >
      <span className="font-medium">Layers:</span>
      {layerNames.map((name) => {
        const visible = !hiddenLayers.has(name);
        const count = buckets[name].length;
        return (
          <span key={name} className="flex items-center gap-1">
            <input
              type="checkbox"
              data-testid={`layer-toggle-${name}`}
              checked={visible}
              onChange={() => onToggle(name)}
              className="cursor-pointer"
              aria-label={`Toggle layer ${name}`}
            />
            <button
              type="button"
              data-testid={`layer-fit-${name}`}
              onClick={() => onFitToLayer(name)}
              className="hover:underline cursor-pointer"
              title={`Fit to ${name} (${count} feature${count === 1 ? '' : 's'})`}
            >
              {name}
              {count > 1 && (
                <span className={`ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  ({count})
                </span>
              )}
            </button>
          </span>
        );
      })}
    </div>
  );
};
